import { DURATION, EASE } from "@/lib/motion"
import { formatBRL } from "@/lib/format-brl"
import { MotionValue, animate, useMotionValue, useReducedMotion, useTransform } from "motion/react"
import { useEffect, useRef } from "react"

const COUNT_DURATION = DURATION.emphasis

/**
 * Counts a money figure from its previous value to the new one.
 *
 * Returns a MotionValue meant to be rendered straight into a `motion.*`
 * element — the ~30 frames of the count then never re-render the component
 * (and, in the bag's case, the whole cart subtree) once per frame.
 *
 *     const total = useAnimatedCurrency(cart.totalPrice)
 *     return <motion.strong>{total}</motion.strong>
 */
export function useAnimatedCurrency(value: number): MotionValue<string> {
  const amount = useAnimatedNumber(value)
  return useTransform(amount, (current) => formatBRL(current))
}

/** Same count, rendered as the bag's "N itens" label (singular included). */
export function useAnimatedItemCount(value: number): MotionValue<string> {
  const count = useAnimatedNumber(value)
  return useTransform(count, (current) => {
    const rounded = Math.round(current)
    return rounded === 1 ? "1 item" : `${rounded} itens`
  })
}

function useAnimatedNumber(value: number): MotionValue<number> {
  const reduceMotion = useReducedMotion()
  const motionValue = useMotionValue(value)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current || reduceMotion) {
      isFirstRender.current = false
      motionValue.set(value)
      return
    }

    const controls = animate(motionValue, value, { duration: COUNT_DURATION, ease: EASE.enter })
    return () => controls.stop()
  }, [value, reduceMotion, motionValue])

  return motionValue
}
